/**
 * PublicProfile.jsx - Public User Profile Page
 * 
 * Displays a user's public profile page with their links and customized theme.
 * This is the main product - what users share with their audience.
 * 
 * @route /:username
 * @auth Not required
 * @params
 * - username: Dynamic URL parameter for user identification
 * @queryParams
 * - preview: Boolean flag to show preview mode bar
 * - tab: Dashboard tab to return to (used with preview mode)
 * 
 * @features
 * - Dynamic theme application based on user settings
 * - Social media icons with links
 * - Custom link buttons
 * - Responsive design
 * - Preview mode for logged-in users
 * - Footer branding based on subscription tier
 * 
 * @dataFlow
 * 1. Fetches user data based on username param
 * 2. Applies theme preset and custom styles
 * 3. Renders profile with conditional elements based on tier
 * 
 * @themeApplication
 * - Loads theme preset from themePresets
 * - Applies custom colors if Pro/Max tier
 * - Injects custom CSS if Max tier
 * 
 * @tierFeatures
 * Free:
 * - Basic themes only
 * - Default footer branding: "Hosted by Link Spaghetti ❤️"
 * 
 * Pro:
 * - Custom colors
 * - Custom footer text (up to 100 characters)
 * - SEO meta tags
 * 
 * Max:
 * - Full CSS customization
 * - Custom footer text (up to 100 characters)
 * - Advanced SEO features
 * 
 * @components
 * - SocialIcons: Platform-specific SVG icons
 * - Dynamic theme application
 * - Conditional footer rendering
 * 
 * @seo
 * - Dynamic page title (Pro/Max)
 * - Meta description (Pro/Max)
 * - Open Graph tags (Pro/Max)
 */

import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { SocialIcons } from '../components/SocialIcons';
import { getPublicProfile, getLinks, getSocialLinks, getUserTheme, trackLinkClick, trackProfileView, getUserProfile, getFooterBranding, getPublicSeoData } from '../services/api.js';
import { getThemesForTier } from '../data/themePresets';
import { analyticsService } from '../services/analytics';

const PublicProfile = () => {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPreview = searchParams.get('preview') === 'true';
  const returnTab = searchParams.get('tab') || 'profile';
  
  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Function to update SEO meta tags
  const updateSeoMetaTags = (seoData, username) => {
    if (typeof document !== 'undefined') {
      // Update document title
      document.title = seoData.title || `${username}'s Profile | Link Spaghetti`;
      
      // Helper function to set or update meta tags
      const setMetaTag = (name, content, property = false) => {
        if (!content) return;
        
        const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
        let meta = document.querySelector(selector);
        
        if (meta) {
          meta.setAttribute('content', content);
        } else {
          meta = document.createElement('meta');
          if (property) {
            meta.setAttribute('property', name);
          } else {
            meta.setAttribute('name', name);
          }
          meta.setAttribute('content', content);
          document.head.appendChild(meta);
        }
      };
      
      // Set basic SEO meta tags
      setMetaTag('description', seoData.description);
      setMetaTag('keywords', seoData.keywords);
      setMetaTag('robots', seoData.robots || 'index,follow');
      
      // Set OpenGraph meta tags
      setMetaTag('og:title', seoData.og_title, true);
      setMetaTag('og:description', seoData.og_description, true);
      setMetaTag('og:image', seoData.og_image, true);
      setMetaTag('og:url', seoData.og_url || window.location.href, true);
      setMetaTag('og:type', 'profile', true);
      
      // Set Twitter Card meta tags
      setMetaTag('twitter:card', seoData.twitter_card || 'summary_large_image');
      setMetaTag('twitter:title', seoData.twitter_title);
      setMetaTag('twitter:description', seoData.twitter_description);
      setMetaTag('twitter:image', seoData.twitter_image);
    }
  };
  
  // Helper function to convert backend theme format to frontend format
  const convertBackendThemeToFrontend = (backendTheme) => {
    if (!backendTheme) return null;
    
    // If it's already in frontend format, return as is
    if (backendTheme.preset || backendTheme.colors) {
      return backendTheme;
    }
    
    // Convert backend format to frontend format
    const mode = backendTheme.mode || 'light';
    const preset = mode === 'dark' ? 'dark' : 'default';
    
    return {
      preset: preset,
      colors: {
        primary: backendTheme.accentColor || '#8b5cf6',
        secondary: backendTheme.accentColor || '#ec4899', 
        background: backendTheme.backgroundColor || '#ffffff',
        text: backendTheme.textColor || '#111827',
        buttonBg: backendTheme.accentColor || '#8b5cf6',
        buttonText: backendTheme.mode === 'dark' ? '#ffffff' : '#ffffff',
        linkBg: backendTheme.mode === 'dark' ? '#2f3132' : '#ffffff',
        linkText: backendTheme.textColor || '#111827',
        linkHover: backendTheme.accentColor || '#8b5cf6',
      },
      customColors: {
        primary: backendTheme.accentColor,
        background: backendTheme.backgroundColor,
        text: backendTheme.textColor,
      }
    };
  };
  
  // Get the selected theme
  const availableThemes = profileData ? getThemesForTier(profileData.tier) : [];
  
  // Convert backend theme to frontend format if needed
  const convertedTheme = profileData?.theme ? convertBackendThemeToFrontend(profileData.theme) : null;
  
  // Debug theme conversion
  if (import.meta.env.DEV && profileData) {
    console.log('🎨 Theme Conversion Debug:');
    console.log('🔄 Raw theme data:', profileData.theme);
    console.log('✅ Converted theme:', convertedTheme);
    console.log('📋 Available themes:', availableThemes.map(t => t.id));
  }
  
  const currentTheme = (convertedTheme && convertedTheme.preset && availableThemes.find(t => t.id === convertedTheme.preset))
    || availableThemes[0] 
    || { 
        id: 'default', 
        name: 'Default', 
        colors: {
          primary: '#8b5cf6',
          secondary: '#ec4899',
          background: '#ffffff',
          text: '#111827',
          buttonBg: '#8b5cf6',
          buttonText: '#ffffff',
          linkBg: '#f3f4f6',
          linkText: '#111827',
          linkHover: '#8b5cf6',
        },
        font: 'Inter'
      };
  
  // Use custom colors if Pro/Max user has customized them, or use converted backend theme colors
  const themeColors = (profileData && profileData.tier !== 'free' && convertedTheme && convertedTheme.customColors)
    ? { ...currentTheme.colors, ...convertedTheme.customColors }
    : (convertedTheme && convertedTheme.colors)
    ? convertedTheme.colors
    : currentTheme.colors || {};

  // Apply custom CSS for Max tier users
  useEffect(() => {
    const themeToUse = convertedTheme || profileData?.theme;
    if (profileData && profileData.tier === 'max' && themeToUse && themeToUse.customCSS) {
      const styleElement = document.createElement('style');
      styleElement.innerHTML = themeToUse.customCSS;
      document.head.appendChild(styleElement);
      
      return () => {
        document.head.removeChild(styleElement);
      };
    }
  }, [profileData, convertedTheme]);

  // Fetch public profile data from backend
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        
        // Check for missing username
        if (!username && !isPreview) {
          setError('Username is required to view a profile.');
          setIsLoading(false);
          return;
        }
        
        if (isPreview) {
          // For preview mode, use authenticated API calls to get current user's data
          const [links, socialLinks, userTheme, footerBranding] = await Promise.all([
            getLinks(),
            getSocialLinks(),
            getUserTheme().catch(() => null),
            getFooterBranding().catch(() => ({ text: 'Hosted by Link Spaghetti ❤️' })),
          ]);
          
          // Get the current user's profile data instead of public API
          let data;
          try {
            // Try to get authenticated user profile first
            data = await getUserProfile();
          } catch (error) {
            // Fallback to public API if getUserProfile fails
            data = await getPublicProfile(username);
          }
          
          // Debug theme data in preview mode
          if (import.meta.env.DEV) {
            console.log('🎨 Preview Mode - Theme Data:');
            console.log('📄 Profile data:', data);
            console.log('🎨 User theme from API:', userTheme);
            console.log('💾 Data theme:', data.theme);
            console.log('📝 Footer branding from API:', footerBranding);
          }
          
          // Transform and structure the preview data properly
          const transformedData = {
            id: data.id,
            username: data.username,
            name: data.name || 'User',
            bio: data.bio || '',
            email: data.email,
            tier: data.tier || 'free',
            profileImage: data.profile_image,
            bannerImage: data.banner_image,
            socialLinks: socialLinks || {},
            links: Array.isArray(links) ? links : [],
            theme: userTheme || data.theme || { preset: 'default' }, // Use theme API data, fallback to profile theme, then default
            seo: data.seo || {},
            analytics: data.analytics || {},
            footerBranding: footerBranding || data.footerBranding || { text: 'Hosted by Link Spaghetti ❤️' }
          };
          
          // Ensure social links have proper structure
          const defaultSocialLinks = {
            twitter: '',
            instagram: '',
            linkedin: '',
            github: '',
            youtube: '',
            tiktok: ''
          };
          const formattedSocialLinks = { ...defaultSocialLinks, ...transformedData.socialLinks };
          
          const profileData = { 
            ...transformedData,
            socialLinks: formattedSocialLinks,
            links: transformedData.links || []
          };
          
          setProfileData(profileData);
          
          // Load and apply SEO data for Pro/Max users in preview mode
          if (profileData.tier !== 'free') {
            try {
              const seoData = await getPublicSeoData(profileData.username);
              updateSeoMetaTags(seoData, profileData.username);
            } catch (error) {
              console.warn('SEO data not available:', error);
            }
          }
        } else {
          // Try public profile endpoint
          const publicProfile = await getPublicProfile(username);
          
          // Check if this is a mock response due to backend issues
          if (publicProfile.mock) {
            setError(publicProfile.error || 'Public profile is not available');
            setIsLoading(false);
            return;
          }
          
          // Transform backend snake_case to frontend camelCase
          const transformedProfile = {
            id: publicProfile.id,
            username: publicProfile.username,
            name: publicProfile.name || 'User',
            bio: publicProfile.bio || '',
            email: publicProfile.email,
            tier: publicProfile.tier || 'free',
            profileImage: publicProfile.profile_image,
            bannerImage: publicProfile.banner_image,
            socialLinks: publicProfile.social_links || {},
            links: publicProfile.custom_links || [],
            theme: publicProfile.theme || { preset: 'default' },
            seo: publicProfile.seo || {},
            analytics: publicProfile.analytics || {},
            footerBranding: publicProfile.footer_branding || { text: 'Hosted by Link Spaghetti ❤️' }
          };
          
          // Ensure social links have proper structure
          const defaultSocialLinks = {
            twitter: '',
            instagram: '',
            linkedin: '',
            github: '',
            youtube: '',
            tiktok: ''
          };
          const formattedSocialLinks = { ...defaultSocialLinks, ...transformedProfile.socialLinks };
          
          const profileData = {
            ...transformedProfile,
            socialLinks: formattedSocialLinks,
            links: transformedProfile.links || []
          };
          
          setProfileData(profileData);
          
          // Load and apply SEO data for Pro/Max users
          if (profileData.tier !== 'free') {
            try {
              const seoData = await getPublicSeoData(profileData.username);
              updateSeoMetaTags(seoData, profileData.username);
            } catch (error) {
              console.warn('SEO data not available:', error);
            }
          }
          
          // Track profile view for analytics (only for non-preview mode)
          if (!isPreview) {
            try {
              // Note: Profile views are tracked automatically by backend when calling getPublicProfile
              // This is optional additional tracking for enhanced analytics
              await analyticsService.trackProfileView({
                id: profileData.id,
                username: profileData.username,
                userId: profileData.userId || profileData.id,
                total_links: profileData.links?.length || 0,
                social_links: Object.keys(profileData.socialLinks || {}).length,
                tier: profileData.tier,
                referrer: document.referrer || 'direct',
                user_agent: navigator.userAgent,
                timestamp: new Date().toISOString()
              });
            } catch (error) {
              // Silent error handling for analytics
              console.warn('Optional analytics tracking failed:', error);
            }
          }
        }
      } catch (error) {
        if (error.status === 404) {
          setError('Profile not found.');
        } else {
          setError('Failed to load profile.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, [username, isPreview]);

  // Filter out empty social links
  const activeSocialLinks = profileData ? Object.entries(profileData.socialLinks).filter(([_, url]) => url) : [];

  const handleLinkClick = async (url, linkData = null) => {
    try {
      // Track analytics with corrected backend endpoints
      if (linkData) {
        // Use corrected link click tracking
        await trackLinkClick(linkData.id, profileData?.username, {
          url: linkData.url,
          title: linkData.title,
          position: linkData.position || 0,
          referrer: document.referrer || 'direct'
        });
        
        console.log('✅ Link click tracked successfully');
      } else {
        // Track social link click using general analytics tracking
        await analyticsService.trackCustomEvent('social_link_click', {
          url: url,
          profile_id: profileData?.id,
          profile_owner: profileData?.username,
          timestamp: new Date().toISOString()
        });
        
        console.log('✅ Social link click tracked successfully');
      }
    } catch (error) {
      // Silent error handling for analytics
    }

    // Open link
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">❌ {error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-gray-500 text-lg">No profile data available</div>
        </div>
      </div>
    );
  }

  const profileStyles = {
    backgroundColor: themeColors.background,
    color: themeColors.text,
    minHeight: '100vh',
    fontFamily: currentTheme.font || 'Inter, sans-serif',
  };

  const buttonStyles = {
    backgroundColor: themeColors.linkBg,
    color: themeColors.linkText,
    transition: 'all 0.2s',
  };

  const buttonHoverStyles = {
    backgroundColor: themeColors.linkHover,
    color: '#ffffff',
  };

  return (
    <div style={profileStyles}>
      {/* Preview Mode Bar */}
      {isPreview && (
        <div className="bg-gray-900 text-white px-4 py-3 text-center">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Preview Mode</span>
              </div>
              <span className="text-sm text-gray-300 hidden sm:inline">This is how your profile will look when published</span>
            </div>
            <button 
              onClick={() => navigate(`/dashboard?tab=${returnTab}`)}
              className="bg-white text-gray-900 px-4 py-1.5 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to editing
            </button>
          </div>
        </div>
      )}

      {/* Banner */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        {profileData.bannerImage ? (
          <img 
            src={profileData.bannerImage} 
            alt="Banner" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div 
            className="w-full h-full"
            style={{
              background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.secondary} 100%)`
            }}
          />
        )}
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-20 relative z-10 pb-20">
        {/* Profile Info */}
        <div className="text-center mb-8">
          {/* Profile Image */}
          <div className="mb-4">
            {profileData.profileImage ? (
              <img 
                src={profileData.profileImage} 
                alt={profileData.name}
                className="w-32 h-32 md:w-40 md:h-40 rounded-full mx-auto border-4 shadow-xl"
                style={{ borderColor: themeColors.background }}
              />
            ) : (
              <div 
                className="w-32 h-32 md:w-40 md:h-40 rounded-full mx-auto border-4 shadow-xl flex items-center justify-center"
                style={{
                  borderColor: themeColors.background,
                  background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.secondary} 100%)`
                }}
              >
                <svg className="w-16 h-16 md:w-20 md:h-20 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
          </div>

          {/* Name and Bio */}
          <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: themeColors.text }}>
            {profileData.name}
          </h1>
          <p className="mb-6 max-w-md mx-auto" style={{ color: themeColors.text, opacity: 0.8 }}>
            {profileData.bio}
          </p>

          {/* Social Links */}
          {activeSocialLinks.length > 0 && (
            <div className="flex justify-center gap-4 mb-8">
              {activeSocialLinks.map(([platform, url]) => (
                <button
                  key={platform}
                  onClick={() => handleLinkClick(url)}
                  className="w-12 h-12 rounded-full shadow-md hover:shadow-lg transform hover:scale-110 transition-all duration-200 flex items-center justify-center"
                  style={{
                    backgroundColor: themeColors.linkBg,
                    color: themeColors.primary,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = themeColors.primary;
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = themeColors.linkBg;
                    e.currentTarget.style.color = themeColors.primary;
                  }}
                  aria-label={`Visit ${platform} profile`}
                >
                  {SocialIcons[platform]}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Custom Links */}
        <div className="space-y-4">
          {profileData.links
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((link, index) => (
            <button
              key={link.id || `link-${index}`}
              onClick={() => handleLinkClick(link.url, { ...link, position: index + 1 })}
              className="w-full p-4 rounded-xl shadow-md hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 font-medium text-center"
              style={buttonStyles}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, buttonHoverStyles);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, buttonStyles);
              }}
            >
              {link.title}
            </button>
          ))}
        </div>
      </div>

      {/* Footer - Shows footer branding for all users, customizable for Pro+ */}
      <div className="fixed bottom-0 left-0 right-0 py-4 text-center">
        <a 
          href="/" 
          className="inline-flex items-center gap-2 text-sm transition-colors"
          style={{ 
            color: themeColors.text, 
            opacity: 0.6,
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
        >
          <span>{profileData.footerBranding?.text || 'Hosted by Link Spaghetti ❤️'}</span>
        </a>
      </div>
    </div>
  );
};

export default PublicProfile;